import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Lock, Globe2, Users2, X, Search, Sparkles } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const MODES = [
  { key: "admin_only", label: "Admin Only", icon: Lock },
  { key: "all", label: "All Customers", icon: Globe2 },
  { key: "selected", label: "Selected Customers", icon: Users2 },
];

export function ActivationModeSwitch({ api, module, onChange }) {
  const [draft, setDraft] = useState(module.mode || "admin_only");
  const [busy, setBusy] = useState(false);
  const [confirmAll, setConfirmAll] = useState(false);

  useEffect(() => { setDraft(module.mode || "admin_only"); }, [module.mode]);

  const apply = async (mode, user_ids = []) => {
    setBusy(true);
    try {
      const r = await api.post("/admin/vcb/module/mode", { mode, user_ids, note: `Mode → ${mode}` });
      toast.success(
        mode === "admin_only" ? "VCB is now ADMIN ONLY · offline for customers"
        : mode === "all" ? "VCB is LIVE for all customers"
        : `VCB live for ${r.data.selected_users?.length || 0} selected customer(s)`,
        { description: mode === "selected" && r.data.emails_sent ? `${r.data.emails_sent} activation email(s) sent` : undefined });
      await onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Mode change failed"); }
    finally { setBusy(false); setConfirmAll(false); }
  };

  const pick = (mode) => {
    if (busy) return;
    setDraft(mode);
    if (mode === "admin_only" && module.mode !== "admin_only") apply("admin_only");
    if (mode === "all" && module.mode !== "all") setConfirmAll(true);
  };

  const active = module.mode || "admin_only";
  const tone = active === "admin_only" ? "#E5484D" : "#2FB999";

  return (
    <div className="rounded-sm border border-[#D8B23E]/40 bg-[#0A0906] p-5 md:p-6 space-y-5" data-testid="vcb-master-switch">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#D8B23E]">
            Master Activation · Customer Visibility
          </div>
          <div className="font-serif text-2xl md:text-3xl text-[#F5F5F0] mt-0.5 inline-flex items-center gap-3">
            <span className="w-2.5 h-2.5 rounded-full animate-pulse" style={{ background: tone }} />
            VCB Module · {active === "admin_only" ? "OFFLINE · admin-only"
              : active === "all" ? "LIVE for all customers"
              : `LIVE · ${module.selected_users?.length || 0} selected`}
          </div>
        </div>
        <div className="inline-flex border border-[#D8B23E]/40 rounded-sm overflow-hidden" role="radiogroup" data-testid="vcb-mode-toggle">
          {MODES.map(({ key, label, icon: Icon }) => {
            const on = draft === key;
            return (
              <button key={key} role="radio" aria-checked={on} disabled={busy}
                      data-testid={`vcb-mode-${key}`}
                      onClick={() => pick(key)}
                      className={`inline-flex items-center gap-2 px-5 py-3 font-grotesk text-[10px] uppercase tracking-[0.24em] transition-colors duration-200 border-r border-[#D8B23E]/20 last:border-r-0 ${
                        on ? "bg-[#D8B23E] text-black" : "text-[#C9C9C2] hover:bg-white/5"}`}>
                <Icon size={12} /> {label}
                {key === active && !on && <span className="w-1.5 h-1.5 rounded-full bg-[#2FB999]" />}
              </button>
            );
          })}
        </div>
      </div>

      {draft === "admin_only" && (
        <div className="text-[11px] text-[#8A8A82] font-body italic max-w-2xl" data-testid="vcb-mode-panel-admin">
          Until you flip this switch, no partner or customer will see the VCB module anywhere — customer-facing URLs return 404. Admin desk stays fully accessible.
        </div>
      )}
      {draft === "all" && (
        <div className="text-[11px] text-[#8A8A82] font-body italic max-w-2xl" data-testid="vcb-mode-panel-all">
          {active === "all"
            ? `Every buyer / seller / custodian can see and use VCB. Activated by ${module.enabled_by || "admin"}${module.enabled_at ? ` on ${new Date(module.enabled_at).toLocaleString()}` : ""}.`
            : "Confirm in the dialog to go live for every onboarded customer simultaneously."}
          {active !== "all" && (
            <Button size="sm" variant="outline" onClick={() => setConfirmAll(true)} data-testid="vcb-mode-all-reopen"
                    className="ml-3 border-[#D8B23E]/40 text-[#D8B23E] font-grotesk uppercase tracking-[0.22em] text-[9px] not-italic">
              Review &amp; Confirm
            </Button>
          )}
        </div>
      )}
      {draft === "selected" && (
        <>
          {active === "selected" && <SelectedRoster api={api} module={module} onChange={onChange} />}
          <SelectedCustomersPanel api={api} module={module} busy={busy} onGoLive={(ids) => apply("selected", ids)} />
        </>
      )}

      <Dialog open={confirmAll} onOpenChange={(o) => { setConfirmAll(o); if (!o) setDraft(active); }}>
        <DialogContent data-testid="vcb-confirm-all-dialog" className="bg-[#050505] border border-[#D8B23E]/60 text-[#F5F5F0] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Activate for All Customers</DialogTitle>
            <DialogDescription className="text-[12px] font-body text-[#C9C9C2]">
              This will make VCB visible to all customers. Confirm?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" data-testid="vcb-confirm-all-cancel" onClick={() => { setConfirmAll(false); setDraft(active); }}
                    className="border-white/20 text-[#C9C9C2] font-grotesk uppercase tracking-[0.24em] text-[10px]">Cancel</Button>
            <Button data-testid="vcb-confirm-all-confirm" disabled={busy} onClick={() => apply("all")}
                    className="bg-[#D8B23E] text-black font-grotesk uppercase tracking-[0.24em] text-[10px]">
              {busy ? <Loader2 size={11} className="animate-spin" /> : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SelectedRoster({ api, module, onChange }) {
  const [busy, setBusy] = useState(null);
  const roster = module.selected_users || [];

  const revoke = async (c) => {
    if (!confirm(`Revoke VCB access for ${c.email}?\n\nTheir VCB URLs will return 404 immediately.`)) return;
    setBusy(c.id);
    try {
      await api.post(`/admin/vcb/module/selected/${c.id}/revoke`);
      toast.success(`Access revoked · ${c.email}`);
      await onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Revoke failed"); }
    finally { setBusy(null); }
  };

  return (
    <div className="rounded-sm border border-[#2FB999]/30 bg-[#02150F]/40 p-4" data-testid="vcb-selected-roster">
      <div className="font-grotesk text-[9px] uppercase tracking-[0.26em] text-[#2FB999] mb-2">
        Live Roster · {roster.length} customer{roster.length === 1 ? "" : "s"} with VCB access
      </div>
      {roster.length === 0 ? (
        <div className="text-[11px] text-[#8A8A82] font-body italic">Roster is empty — VCB is effectively offline for customers.</div>
      ) : (
        <div className="divide-y divide-white/5">
          {roster.map((c) => (
            <div key={c.id} className="flex items-center justify-between gap-3 py-2" data-testid={`vcb-roster-row-${c.id}`}>
              <div className="min-w-0">
                <div className="font-body text-sm text-[#F5F5F0] truncate">{c.email}</div>
                {c.name && <div className="text-[10px] text-[#8A8A82] font-body italic">{c.name}</div>}
              </div>
              <Button size="sm" variant="outline" disabled={busy === c.id} onClick={() => revoke(c)}
                      data-testid={`vcb-roster-revoke-${c.id}`}
                      className="border-[#E5484D]/50 text-[#E5484D] hover:bg-[#E5484D]/10 font-grotesk uppercase tracking-[0.2em] text-[9px] shrink-0">
                {busy === c.id ? <Loader2 size={10} className="animate-spin" /> : <><X size={10} className="mr-1" /> Revoke</>}
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SelectedCustomersPanel({ api, module, busy, onGoLive }) {
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(() => new Map((module.selected_users || []).map((s) => [s.id, s])));

  useEffect(() => { setPicked(new Map((module.selected_users || []).map((s) => [s.id, s]))); }, [module.selected_users]);

  useEffect(() => {
    api.get("/admin/vcb/module/customers").then((r) => setCustomers(r.data?.customers || []))
      .catch(() => toast.error("Could not load customers"));
  }, [api]);

  const results = useMemo(() => {
    const s = q.trim().toLowerCase();
    return customers.filter((c) => !picked.has(c.id) && (!s || c.email?.toLowerCase().includes(s) || c.name?.toLowerCase().includes(s))).slice(0, 40);
  }, [customers, q, picked]);

  const add = (c) => { setPicked((p) => new Map(p).set(c.id, c)); setQ(""); setOpen(false); };
  const remove = (id) => setPicked((p) => { const n = new Map(p); n.delete(id); return n; });
  const ids = [...picked.keys()];
  const liveIds = new Set(module.mode === "selected" ? module.selected_user_ids || [] : []);
  const dirty = ids.length !== liveIds.size || ids.some((i) => !liveIds.has(i));

  return (
    <div className="space-y-3" data-testid="vcb-mode-panel-selected">
      <div className="relative max-w-xl">
        <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A82]" />
        <Input data-testid="vcb-customer-search" value={q} placeholder="Search onboarded customers by name or email…"
               onChange={(e) => { setQ(e.target.value); setOpen(true); }}
               onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
               className="pl-8 bg-black/50 border-white/20 text-[#F5F5F0]" />
        {open && results.length > 0 && (
          <div className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto rounded-sm border border-[#D8B23E]/40 bg-[#0A0906] shadow-xl" data-testid="vcb-customer-dropdown">
            {results.map((c) => (
              <button key={c.id} onMouseDown={(e) => e.preventDefault()} onClick={() => add(c)}
                      data-testid={`vcb-customer-option-${c.id}`}
                      className="w-full text-left px-3 py-2 hover:bg-[#D8B23E]/10 border-b border-white/5 last:border-0">
                <div className="font-body text-sm text-[#F5F5F0]">{c.email}</div>
                <div className="text-[10px] text-[#8A8A82] font-grotesk uppercase tracking-[0.18em]">{c.name || "—"} · {c.role}</div>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-2 min-h-[28px]" data-testid="vcb-selected-tags">
        {ids.length === 0 && <span className="text-[11px] text-[#8A8A82] font-body italic">No customers selected yet.</span>}
        {[...picked.values()].map((c) => (
          <span key={c.id} data-testid={`vcb-selected-tag-${c.id}`}
                className="inline-flex items-center gap-1.5 border border-[#D8B23E]/60 bg-[#D8B23E]/10 text-[#D8B23E] px-2.5 py-1 rounded-sm font-grotesk text-[10px] uppercase tracking-[0.18em]">
            {c.email}
            <button onClick={() => remove(c.id)} data-testid={`vcb-selected-tag-remove-${c.id}`} className="hover:text-white" aria-label={`Remove ${c.email}`}><X size={11} /></button>
          </span>
        ))}
      </div>
      <Button data-testid="vcb-go-live-selected" disabled={busy || ids.length === 0 || !dirty} onClick={() => onGoLive(ids)}
              className="bg-[#D8B23E] text-black font-grotesk uppercase tracking-[0.28em] text-[10px] hover:bg-[#E5C450] px-6 py-5">
        {busy ? <Loader2 size={12} className="animate-spin" /> : <><Sparkles size={12} className="mr-1.5" /> Go Live for Selected</>}
      </Button>
    </div>
  );
}
